<div class="tw-flex tw-flex-col tw-w-full tw-h-full tw-gap-4 tw-py-4">
  <!-- Page header -->
  <div class="tw-flex tw-items-center tw-gap-3">
    <button
      type="button"
      class="tw-flex tw-items-center tw-justify-center tw-h-8 tw-w-8 tw-rounded-lg tw-border-none tw-bg-transparent tw-p-0 tw-cursor-pointer hover:tw-bg-color-surface-hover"
      aria-label="Go back"
      (click)="goBack()"
    >
      <icon name="left-arrow" [size]="4" currentColor="color-icon-default"></icon>
    </button>
    <h1 class="tw-text-base tw-font-medium tw-text-color-text-default tw-m-0">
      Recurring Transactions
    </h1>
  </div>

  <div class="tw-grid tw-grid-cols-2 tw-gap-4 tw-flex-1 tw-min-h-0 sm:tw-grid-cols-1">
    <!-- Left card · recurring patterns across all accounts -->
    <div
      appGlossScroll
      class="tw-flex tw-flex-col tw-gap-4 tw-min-h-0 tw-overflow-auto tw-bg-color-surface-card tw-rounded-2xl tw-p-4 tw-shadow-elevation-card"
    >
      @if (!hasAccounts) {
        <div class="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-pt-8 tw-text-center">
          <div
            class="tw-flex tw-items-center tw-justify-center tw-h-14 tw-w-14 tw-rounded-full tw-bg-color-surface-sunken"
          >
            <icon name="photo" [size]="5" currentColor="color-icon-secondary"></icon>
          </div>
          <h2
            class="tw-font-heading tw-text-body-md tw-font-semibold tw-text-color-text-default tw-m-0"
          >
            Connect an account to start
          </h2>
          <p class="tw-font-body tw-text-caption tw-text-color-text-secondary tw-m-0 tw-max-w-xl">
            Recurring Assistant reads your transaction history to find subscriptions and bills.
            Connect at least one account to begin.
          </p>
          <button type="button" [class]="connectButtonClass" (click)="goToAccounts()">
            Connect an account
          </button>
        </div>
      } @else {
        <div class="tw-flex tw-items-center tw-justify-end">
          <button
            type="button"
            class="tw-text-xs tw-underline tw-text-color-text-secondary tw-border-none tw-bg-transparent tw-cursor-pointer"
            (click)="resetOverrides()"
          >
            Reset overrides
          </button>
        </div>

        <!-- Recurring payments table -->
        <div>
          <h3 class="tw-text-sm tw-font-semibold tw-mb-2">Recurring Payments</h3>
          <app-ag-grid-table
            [options]="recurringGridOptions"
            [data]="matchedRowData"
            [hasHeader]="true"
          ></app-ag-grid-table>
        </div>

        <!-- Other payments table -->
        <div>
          <h3 class="tw-text-sm tw-font-semibold tw-mb-2">Other Payments</h3>
          <app-ag-grid-table
            [options]="otherGridOptions"
            [data]="unmatchedRowData"
            [hasHeader]="true"
          ></app-ag-grid-table>
        </div>
      }
    </div>

    <!-- Right card · spend summary -->
    <div
      class="tw-flex tw-flex-col tw-gap-4 tw-min-h-0 tw-bg-color-surface-card tw-rounded-2xl tw-p-4 tw-shadow-elevation-card"
    >
      <div class="tw-flex tw-items-center tw-gap-2">
        <div
          class="tw-flex tw-items-center tw-justify-center tw-h-7 tw-w-7 tw-rounded-full tw-bg-color-surface-sunken"
        >
          <icon name="dollar" [size]="4" currentColor="color-icon-secondary"></icon>
        </div>
        <h2 class="tw-font-heading tw-text-lg tw-font-medium tw-text-color-text-default tw-m-0">
          Total Spend
        </h2>
        <span class="tw-flex-1"></span>
        <app-dropdown-select
          class="tw-w-[150px] tw-shrink-0"
          [options]="periodOptions"
          [selectedOption]="selectedPeriod"
          [compact]="true"
          [flat]="true"
          [controlHeightClass]="'tw-h-10'"
          [arrowSize]="5"
          [customShadowClass]="
            'tw-rounded-[var(--radius-xl)] tw-border-none tw-bg-color-surface-sunken tw-shadow-elevation-input tw-px-4 tw-text-caption tw-text-color-text-default tw-whitespace-nowrap'
          "
          [panelClass]="'tw-mt-1.5 tw-rounded-lg'"
          testLabel="spend-period"
          (optionSelected)="onPeriodChange($event)"
        ></app-dropdown-select>
      </div>

      <div class="tw-flex tw-flex-col tw-gap-1">
        <div class="tw-flex tw-items-baseline tw-gap-2">
          <span class="tw-font-number tw-text-h3 tw-leading-none tw-text-color-text-default">
            {{ projectedSpend | number: "1.0-2" }}
          </span>
          <span class="tw-font-body tw-text-label tw-text-color-text-secondary">{{
            displayCurrency
          }}</span>
        </div>
        <span class="tw-font-body tw-text-body-sm tw-text-color-text-tertiary"
          >Projected for this period</span
        >
      </div>

      <div class="tw-grid tw-grid-cols-2 tw-gap-3 xs:tw-grid-cols-1">
        <div
          class="tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-p-3 tw-bg-gradient-to-tr tw-from-color-surface-input tw-to-[color-mix(in_srgb,var(--color-accent-today)_20%,transparent)]"
        >
          <div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
            <div
              class="tw-flex tw-items-center tw-justify-center tw-gap-[9.7px] tw-h-10 tw-w-10 tw-rounded-[var(--radius-xl)] tw-bg-color-surface-brand"
            >
              <icon name="pie-chart" [size]="6" currentColor="color-icon-action"></icon>
            </div>
            <span class="tw-font-body tw-text-label tw-text-color-text-secondary"
              >Active Subscriptions</span
            >
          </div>
          <span class="tw-font-heading tw-text-h4 tw-font-semibold tw-text-color-text-default">
            {{ matchedRowData.length }}
          </span>
        </div>
        <div
          class="tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-p-3 tw-bg-gradient-to-tr tw-from-color-surface-input tw-to-[color-mix(in_srgb,var(--color-tertiary-700)_20%,transparent)]"
        >
          <div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
            <div
              class="tw-flex tw-items-center tw-justify-center tw-gap-[9.7px] tw-h-10 tw-w-10 tw-rounded-[var(--radius-xl)] tw-bg-color-accent-future-bg"
            >
              <icon name="bar-chart" [size]="6" currentColor="color-status-info-text"></icon>
            </div>
            <span class="tw-font-body tw-text-label tw-text-color-text-secondary">{{
              dueLabel
            }}</span>
          </div>
          <span class="tw-font-heading tw-text-h4 tw-font-semibold tw-text-color-text-default">
            {{ dueInPeriodCount }}
          </span>
        </div>
      </div>
    </div>
  </div>
</div>
